import React, {useCallback} from 'react';
// import qs from 'query-string';
import {
  faPlus,
  faMinus,
  faTrashAlt,
  faPencilAlt
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {CellProps, Column} from 'react-table';
import {ListTable} from 'components/pages/common/table/ListTable';
import {useForm} from 'react-hook-form';
import {useAsync} from "react-async-hook";
import {useLocation} from "react-router-dom";

import {userApi} from "../../apis/admin/user/userApi";
import useAppRouter from "../../hooks/useAppRouter";
import useLoading from '../../hooks/useLoading';
import {config} from "../../libs/config/config";
import {formatOmittedDateTime} from "../../libs/utils/formatDate";
import {logError} from "../../libs/utils/logError";

import { UpdateSalesmanModal } from '../../components/pages/admin/RoomMemberEdit/UpdateSalesmanModal';
import { DeleteSalesmanModal } from '../../components/pages/admin/Account/DeleteSalesmanModal';
import { CreateSalesmanModal } from '../../components/pages/admin/Account/CreateSalesmanModal';

import { DeleteUserModalParams } from '../../types/admin/account';
import {BulkCreateSalesmanModal} from '../../components/pages/admin/Account/BulkCreateSalesmanModal';


interface Salesman {
  userId: number;          // ID
  userName: string;        // 営業ユーザ名
  companyName: string;     // 会社名
  email: string;           // メールアドレス
  department: string;      // 部署
  createDateTime: string;  // 作成日付
}

interface DataType extends Salesman {
  operation?: string  // 操作
}

interface IndexSalesmanParams {
  userName: string;     // 営業ユーザ名
  companyName: string;  // 会社名
  department: string;   // 部署
  email: string;        // メールアドレス
  size: number;         // 表示件数
  currentPage: number;  // ページ番号
  sort: Array<string>;  // ソート
}


export const SalesmanListPage: React.FC = () => {
  const { goto } = useAppRouter();
  const withLoading = useLoading();

  const location = useLocation();
  // const { state: { currentUser } } = useAuth();

  const [salesmanList, setSalesmanList] = React.useState<DataType[]>([]);
  const [isShowSearchCondition, setIsShowSearchCondition] = React.useState<boolean>(false);
  const [abortController, setAbortController] = React.useState<AbortController | undefined>();
  const [deleteUserModalParams, setDeleteUserModalParams] = React.useState<DeleteUserModalParams | undefined>();

  const [selectUserId, setSelectUserId] = React.useState<number | undefined>();
  const [showUpdateSalesmanModal, setShowUpdateSalesmanModal] = React.useState<boolean>(false);
  const [showDeleteUserModal, setShowDeleteUserModal] = React.useState<boolean>(false);
  const [showCreateUserModal, setShowCreateUserModal] = React.useState<boolean>(false);
  const [showBulkCreateUserModal, setShowBulkCreateUserModal] = React.useState<boolean>(false);

  const [totalCount, setTotalCount] = React.useState<number>(10);
  const [params, setParams] = React.useState<IndexSalesmanParams>({
    userName: '',
    companyName: '',
    department: '',
    email: '',
    size: config.list.pageSizeOptions[0],
    currentPage: 1,
    sort: [
      'createDateTime:desc'
    ],
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
  } = useForm({
    reValidateMode: 'onSubmit',
    defaultValues: params
  });

  const reloadSalesmanList = async () => {
    if (abortController !== undefined) {
      abortController.abort();
      setAbortController(undefined);
    }

    setAbortController(new AbortController());

    withLoading(async () => {
      const offset = (params.currentPage - 1) * params.size;

      try {
        const response = await userApi.salesmanList(
          params.userName,
          params.email,
          params.companyName,
          params.department,
          params.size,
          offset,
          params.sort,
          {
            abortSignal: abortController?.signal,
          }
        );

        setValue('userName', params.userName);
        setValue('companyName', params.companyName);
        setValue('department', params.department);
        setValue('email', params.email);

        setSalesmanList(response.salesmen);
        setTotalCount(response.totalHits);
        setAbortController(undefined);

      } catch(e) {
        await logError(
          location.pathname,
          '営業一覧の読み込みに失敗しました。',
          e
        );

        goto('internalServerError');
      }
    });
  }

  useAsync(async () => {
    await reloadSalesmanList();
  }, [params]);

  const onSalesmanUpdateSubmitted = async (isChange: boolean) => {
    if (isChange) {
      await reloadSalesmanList();
    }
    setShowUpdateSalesmanModal(false);
  };

  const onDeleteSalesmanSubmitted = async (isChange: boolean) => {
    if (isChange) {
      await reloadSalesmanList();
    }
    setShowDeleteUserModal(false);
  };

  const onCreateSalesmanSubmitted = async (isChange: boolean) => {
    if (isChange) {
      await reloadSalesmanList();
    }
    setShowCreateUserModal(false);
  };

  const handleFormClear = () => {
    reset();
  }

  const handleSort = async (newSort: string[]) => {
    if ((params.sort.length === newSort.length)
      && params.sort.every((sort, index) => sort === newSort[index])) {
      return
    }

    setParams(prevState => ({
      ...prevState,
      sort: newSort,
      currentPage: 1,
    }));
  }

  const handlePageChange = async (selectedItem: { selected: number }) => {
    setParams(prevState => ({
      ...prevState,
      currentPage: selectedItem.selected + 1,
    }));
  }

  const handlePageSizeChange = async (newSize: number) => {
    setParams(prevState => ({
      ...prevState,
      size: newSize,
      currentPage: 1,
    }));
  }

  const handleSearchFormSubmit = async (data: any) => {
    setParams(prevState => ({
      ...prevState,
      userName: data.userName,
      companyName: data.companyName,
      department: data.department,
      email: data.email,
      currentPage: 1,
    }));
  }

  const handleSalesmanEdited = async () => {
    await reloadSalesmanList();
  }

  const OperationCell = React.useCallback(({ row }: CellProps<Salesman>) => (
    <>
    <div className="grid gap-1">
      <button className="btn-primary"
              type='button'
              onClick={() => {
                setSelectUserId(row.original.userId);
                setShowUpdateSalesmanModal(true);
              }}
      >
        <FontAwesomeIcon className="mr-2" icon={faPencilAlt} />編 集
      </button>
      <button className="btn-primary"
              type='button'
              onClick={() => {
                setDeleteUserModalParams({
                  userId: row.original.userId,
                  userName: row.original.userName,
                });
                setShowDeleteUserModal(true);
              }}
      >
        <FontAwesomeIcon className="mr-2" icon={faTrashAlt} />削 除
      </button>
    </div>
    </>
  ), []);

  const DateTimeCell = useCallback(({ value }: CellProps<DataType>) => (
    <div>{ value ? formatOmittedDateTime(value) : '' }</div>
  ), []);

  const UserNameCell = useCallback(({ value }: CellProps<DataType>) => (
    <div className="user-name">{ value }</div>
  ), []);

  const WordWrapCell = useCallback(({ value }: CellProps<DataType>) => (
    <div className="break-all">{ value }</div>
  ), []);


  const columns: Column<DataType>[] = React.useMemo(
    () => [
      {
        Header: 'ID',
        accessor: 'userId',
        disableSortBy: true,
      },
      {
        Header: '営業担当名',
        accessor: 'userName',
        disableSortBy: true,
        Cell: UserNameCell,
      },
      {
        Header: 'メールアドレス',
        accessor: 'email',
        disableSortBy: true,
        Cell: WordWrapCell,
      },
      {
        Header: '作成日時',
        accessor: 'createDateTime',
        Cell: DateTimeCell,
      },
      {
        Header: '操作',
        accessor: 'operation',
        Cell: OperationCell,
        disableSortBy: true,
      },
    ],
    [OperationCell, WordWrapCell, UserNameCell, DateTimeCell]
  );


  return (
    <>
      {showUpdateSalesmanModal && (
        <UpdateSalesmanModal
          {...{
            userId: selectUserId,
            onSubmitted: onSalesmanUpdateSubmitted,
          }}
        />
      )}

      {showDeleteUserModal && (
        <DeleteSalesmanModal
          {...{
            modalParams: deleteUserModalParams,
            onSubmitted: onDeleteSalesmanSubmitted,
          }}
        />
      )}

      {showCreateUserModal && (
        <CreateSalesmanModal
          {...{
            onSubmitted: onCreateSalesmanSubmitted,
          }}
        />
      )}

      {showBulkCreateUserModal && (
        <BulkCreateSalesmanModal
         onClosed={async () => setShowBulkCreateUserModal(false)}
         onCreated={handleSalesmanEdited}/>
      )}


      <div className="bg-sub min-h-full">
        <div className="mx-8 flex items-center py-6">
          <div className="text-xl text-primary font-semibold">営業一覧</div>
        </div>

        <div className="mx-8">
          <form onSubmit={handleSubmit(handleSearchFormSubmit)}>
            <div className="shadow-md border border-white px-6">
              <button
                type="button"
                className="text-primary focus:outline-none w-full"
                onClick={() => setIsShowSearchCondition(prevState => !prevState)}
              >
                <div className="py-4 flex justify-between">
                  <div className="text-primary font-bold">
                      検索条件
                  </div>
                  <div>
                    {isShowSearchCondition ? <FontAwesomeIcon icon={faMinus} /> : <FontAwesomeIcon icon={faPlus}  />}
                  </div>
                </div>
              </button>

              {isShowSearchCondition && (
                <div>
                  <div className="grid grid-cols-2 items-center gap-10">
                    { /* 営業担当名 */ }
                    <div className="mb-4">
                      <label htmlFor="userName">
                        <div className="text-sm font-semibold text-primary">
                          営業担当名
                        </div>
                        <input
                          /* eslint-disable-next-line react/jsx-props-no-spreading */
                          {...register('userName', {})}
                          className="text-field"
                          id="userName"
                          type="text"
                        />
                      </label>
                    </div>

                    { /* メールアドレス */ }
                    <div className="mb-4">
                      <label htmlFor="email">
                        <div className="text-sm font-semibold text-primary">
                          メールアドレス
                        </div>
                        <input
                          /* eslint-disable-next-line react/jsx-props-no-spreading */
                          {...register('email', {})}
                          className="text-field"
                          id="email"
                          type="text"
                        />
                      </label>
                    </div>

                  </div>

                  <div className="p-4 border-gray-200 text-center">
                    <button type="button"
                            className="btn-sub mr-6"
                            onClick={handleFormClear}
                    >
                      クリア
                    </button>
                    <button type="submit"
                            className="btn-primary"
                    >
                      検 索
                    </button>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>

        <div className="m-8 text-right">
          <button type="button"
                  className="btn-primary mr-3"
                  onClick={() => {
                    setShowBulkCreateUserModal(true);
                  }}
          >
            営業担当一括追加
          </button>

          <button type="button"
                  className="btn-primary"
                  onClick={() => {
                    setShowCreateUserModal(true);
                  }}
          >
            営業担当追加
          </button>
        </div>

        <div className="mx-8 my-8">
            <>
              <ListTable
                tableData={salesmanList}
                columns={columns}
                onSort={handleSort}
                initialSort={params.sort}
                totalCount={totalCount}
                pageSize={params.size}
                currentPage={params.currentPage}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </>
        </div>
      </div>
    </>
  );
};
